import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { TravelTwoIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { updateAssignedJourneys } from '../../../../api/centers/update-assigned-journeys'
import { useObserver } from '../../../../lib/contexts/observer'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { Center } from '../../../../lib/types/models/center/center'
import { CenterProperties } from '../../../../lib/types/models/center/properties.center'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import Legend from '../../../elements/legend'
import PeriodBadge from '../../../elements/period-badge'
import CenterJourneySelectionList from '../assign-journey-to-center/center-journey-selection-list'
import CenterGlobalViewStaffRequestSection from './center-global-view-staff-request-section'
import EditHeader from './edit-header'
import InfoCardWithProgress from './Info-card-with-progress'

type Props = {
  center: Center
}

const CenterGlobalViewJourneyTripAndStaffSection = ({ center }: Props) => {
  const [step, setStep] = useState(0)
  const observer = useObserver({ key: 'center-update-global-view' })

  const { selectedData, modalState, toggleModal } = useModalManager<'edit', Center>(['edit'])

  const updateAssignJourneysMutation = useMutation({
    mutationFn: updateAssignedJourneys,
    onError: showAxiosError,
    onSuccess: () => {
      observer.notify(['center-global-view'])
      observer.notify(['refresh-staff-request'])
      setStep(2)
    }
  })

  return (
    <InnerBox className="rounded-xl ">
      <EditHeader
        className="flex items-center justify-between"
        onEditClick={() => toggleModal('edit', center)}
        header={
          <div className="flex items-center gap-4">
            <Legend color={tailwindColors.blue.light.active} text="Affectés" />
            <Legend color={tailwindColors.fushia.light.active} text="Bloqués" />
            <Legend color={tailwindColors.green.light.active} text="Restants" />
          </div>
        }
      />
      <div className="h-4" />
      <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
        Séjours
      </Typography>
      <div className="h-2" />
      <div className="grid grid-cols-3 items-center gap-3">
        {CenterProperties.getCenterJourneyInformation(center).map(
          ({ period, title, journeyId }) => {
            const centerToJourney = center.centerToJourneys.find(
              ({ journey }) => journey.id === journeyId
            )
            if (!centerToJourney) return '-'
            return (
              <InfoCardWithProgress
                key={journeyId}
                affected={centerToJourney.affectedStudentsNumber ?? 0}
                blocked={centerToJourney.blockedPlacesNumber ?? 0}
                total={centerToJourney.maxNumberOfStudentPlaces ?? 0}
                header={<PeriodBadge className="h-10" period={period} text={title} />}
              />
            )
          }
        )}
      </div>
      <div className="h-4" />
      <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
        Voyages
      </Typography>
      <div className="h-2" />
      <div className="grid grid-cols-3 items-center gap-3">
        {center.tripPlacesRequests.map((tripPlacesRequest) => (
          <InfoCardWithProgress
            key={tripPlacesRequest.id}
            affected={tripPlacesRequest.numberOfStudentsPlacesAffected ?? 0}
            blocked={tripPlacesRequest.numberOfBlockedPlaces ?? 0}
            total={tripPlacesRequest.numberOfStudentsPlacesRequested}
            staffCount={tripPlacesRequest.numberOfStaffPlacesRequested ?? 0}
            header={
              <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
                {TripProperties.getTripLabel(tripPlacesRequest.trip)}
              </Typography>
            }
          />
        ))}
      </div>
      <div className="h-4" />
      <CenterGlobalViewStaffRequestSection centerId={center.id} />

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={CenterProperties.getModalTitleBasedOnStep(step)}
          titleAlignment="left"
          size={step === 0 ? 'large' : 'medium'}
          hideActions
          icon={TravelTwoIcon}
          onCancel={() => {
            toggleModal('edit')
            setStep(0)
          }}
        >
          <CenterJourneySelectionList
            step={step}
            onStepChange={setStep}
            centerId={center.id}
            defaultValues={CenterProperties.mapCenterToInput(center).assignJourneyToCenter}
            onSubmit={async (data) => {
              await updateAssignJourneysMutation.mutateAsync({
                id: center.id,
                ...CenterProperties.mapAssignJourneyToCenterToRequestInput(data)
              })
              setStep(0)
              toggleModal('edit')
            }}
          />
        </Modal>
      )}
    </InnerBox>
  )
}

export default CenterGlobalViewJourneyTripAndStaffSection
